import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { getLocalStorageValue } from 'config/helper';

const clamp01 = (n) => Math.min(1, Math.max(0, n));

const parseRate = (raw, fallback) => {
  if (raw == null || raw === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? clamp01(n) : fallback;
};

const isLocalhostHostname = (host) => {
  const h = host.toLowerCase();
  return (
    h === 'localhost' ||
    h.endsWith('.localhost') ||
    h === '127.0.0.1' ||
    h === '::1'
  );
};

export function shouldInstrumentSentry() {
  if (typeof window === 'undefined') return false;
  if (isLocalhostHostname(window.location.hostname)) return false;
  return Boolean(process.env.REACT_APP_SENTRY_DSN?.trim());
}

export function syncSentryUserFromStorage() {
  if (!shouldInstrumentSentry() || !Sentry.getClient()) return;

  const token = getLocalStorageValue('user-token');
  const memberNumber = getLocalStorageValue('member-number');
  const rsaClientId = getLocalStorageValue('RSAclient-id');

  if (token && memberNumber) {
    Sentry.setUser({ id: String(memberNumber) });
    if (rsaClientId) {
      Sentry.setTag('rsa_client_id', String(rsaClientId));
    } else {
      Sentry.getCurrentScope().setTag('rsa_client_id', '');
    }
    return;
  }

  clearSentryUser();
}

export function clearSentryUser() {
  if (!shouldInstrumentSentry() || !Sentry.getClient()) return;
  Sentry.setUser(null);
  Sentry.getCurrentScope().setTag('rsa_client_id', '');
}

export function initSentry() {
  if (!shouldInstrumentSentry()) return;

  const dsn = process.env.REACT_APP_SENTRY_DSN.trim();
  const isProd = process.env.REACT_APP_ENV === 'prod';

  const tracesSampleRate = parseRate(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
    isProd ? 0.12 : 0.35,
  );
  const replaysSessionSampleRate = parseRate(
    process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    isProd ? 0.06 : 0.2,
  );
  const replaysOnErrorSampleRate = parseRate(
    process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    1,
  );

  Sentry.init({
    dsn,
    environment:
      process.env.REACT_APP_SENTRY_ENVIRONMENT ||
      process.env.REACT_APP_ENV ||
      'development',
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    initialScope: (scope) => {
      scope.setTag('host', window.location.hostname);
      return scope;
    },
  });

  syncSentryUserFromStorage();
}

export { Sentry };
